import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import DrillCard from '../components/cards/DrillCard';
import StatCard from '../components/cards/StatCard';
import ChildManagementCard from '../components/ChildManagementCard';
import FamilyLinkRequestModal from '../components/FamilyLinkRequestModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import DashboardNavigation, { AvatarMenu } from '../components/DashboardNavigation';
import MobileHeaderDropdown from '../components/MobileHeaderDropdown';
import ImpersonationBanner from '../components/ImpersonationBanner';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import { formatDate } from '../utils/formatters';
import { COLORS, SPACING } from '../constants/theme';

const ParentDashboardPage = () => {
  const { profile } = useAuth();
  const { isDesktop } = useResponsive();

  // State for children
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for drills and stats
  const [assignedDrills, setAssignedDrills] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);

  // Modal and UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState(null);
  const [playerNotes, setPlayerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showChildManagement, setShowChildManagement] = useState(false);
  const [showLinkRequestModal, setShowLinkRequestModal] = useState(false);
  const [drillVideoUrl, setDrillVideoUrl] = useState(null);
  const [drillThumbnailUrl, setDrillThumbnailUrl] = useState(null);

  // Load children when profile is available
  useEffect(() => {
    if (profile?.id) {
      loadChildren();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // Load video and thumbnail URLs when drill is selected
  useEffect(() => {
    const loadDrillMedia = async () => {
      if (!selectedDrill?.drills) {
        setDrillVideoUrl(null);
        setDrillThumbnailUrl(null);
        return;
      }

      const drill = selectedDrill.drills;

      // Load video URL
      if (drill.video_storage_path) {
        try {
          const { data, error } = await supabase.storage
            .from('drill-videos')
            .createSignedUrl(drill.video_storage_path, 31536000); // 1 year

          if (!error && data?.signedUrl) {
            setDrillVideoUrl(data.signedUrl);
          }
        } catch (err) {
          console.error('Error loading video:', err);
        }
      } else if (drill.video_url) {
        setDrillVideoUrl(drill.video_url);
      } else {
        setDrillVideoUrl(null);
      }

      // Load thumbnail URL
      if (drill.thumbnail_storage_path) {
        try {
          const { data, error } = await supabase.storage
            .from('drill-thumbnails')
            .createSignedUrl(drill.thumbnail_storage_path, 31536000); // 1 year

          if (!error && data?.signedUrl) {
            setDrillThumbnailUrl(data.signedUrl);
          }
        } catch (err) {
          console.error('Error loading thumbnail:', err);
        }
      } else if (drill.thumbnail_url) {
        setDrillThumbnailUrl(drill.thumbnail_url);
      } else {
        setDrillThumbnailUrl(null);
      }
    };

    loadDrillMedia();
  }, [selectedDrill]);

  // Load drills and stats when child is selected
  useEffect(() => {
    if (selectedChild) {
      loadAssignedDrills(selectedChild.id);
      loadPlayerStats(selectedChild.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      setLoading(true);
      setError('');

      // Build the query based on whether parent has a family_id
      let query = supabase.from('profiles').select('*, teams(name)');

      // If parent has family_id, include independent children (via family_id)
      // Always include dependent children (via managed_by)
      if (profile.family_id) {
        query = query.or(
          `and(family_id.eq.${profile.family_id},family_id.not.is.null),managed_by.eq.${profile.id}`
        );
      } else {
        // Parent has no family_id yet, only fetch dependent children
        query = query.eq('managed_by', profile.id);
      }

      const { data, error } = await query
        .neq('id', profile.id) // Exclude parent's own profile
        .order('full_name');

      if (error) throw error;

      const childrenData = data || [];
      setChildren(childrenData);

      // Auto-select first child if available
      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0]);
      }
    } catch (error) {
      console.error('Error loading children:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedDrills = async (childId) => {
    try {
      const { data, error } = await supabase
        .from('assigned_drills')
        .select(
          `
          *,
          drills (
            id,
            name,
            description,
            category,
            difficulty_level,
            video_url,
            video_storage_path,
            video_source,
            thumbnail_url,
            thumbnail_storage_path,
            thumbnail_source,
            duration_minutes
          )
        `
        )
        .eq('player_id', childId)
        .order('due_date', { ascending: true, nullsLast: true })
        .order('assigned_date', { ascending: false });

      if (error) throw error;
      setAssignedDrills(data || []);
    } catch (error) {
      console.error('Error loading assigned drills:', error);
      setError(error.message);
    }
  };

  const loadPlayerStats = async (childId) => {
    try {
      const { data, error } = await supabase
        .from('player_stats')
        .select(
          `
          *,
          category:stat_categories(
            id,
            name,
            unit,
            sort_direction
          )
        `
        )
        .eq('player_id', childId)
        .order('recorded_date', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Load rank information for each stat
      const statsWithRanks = await Promise.all(
        (data || []).map(async (stat) => {
          // Only calculate rank if stat has a category and child has a team
          if (!stat.stat_category_id || !selectedChild?.team_id) {
            return { ...stat, rank: null };
          }

          try {
            // Get rank for this specific stat entry (all stats mode)
            const { data: rankData, error: rankError } = await supabase.rpc(
              'get_player_stat_rank_all',
              {
                p_stat_id: stat.id,
                p_stat_category_id: stat.stat_category_id,
                p_team_id: selectedChild.team_id,
              }
            );

            if (rankError) {
              console.error('Error loading rank:', rankError);
              return { ...stat, rank: null };
            }

            return {
              ...stat,
              rank: rankData && rankData.length > 0 ? rankData[0] : null,
            };
          } catch (err) {
            console.error('Error calculating rank:', err);
            return { ...stat, rank: null };
          }
        })
      );

      setPlayerStats(statsWithRanks);
    } catch (error) {
      console.error('Error loading player stats:', error);
      setError(error.message);
    }
  };

  const handleViewDetails = (drill) => {
    setSelectedDrill(drill);
    setShowDetailsModal(true);
  };

  const handleMarkComplete = (drill) => {
    setSelectedDrill(drill);
    setPlayerNotes('');
    setShowCompletionModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowCompletionModal(false);
    setShowDetailsModal(false);
    setSelectedDrill(null);
    setPlayerNotes('');
    setError('');
    setSuccess('');
  };

  const handleSubmitCompletion = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const updates = {
        completed: true,
        completed_date: new Date().toISOString().split('T')[0],
        player_notes: playerNotes || null,
      };

      // Use selectedChild.id as player_id (parent is acting on behalf of child)
      const { error } = await supabase
        .from('assigned_drills')
        .update(updates)
        .eq('id', selectedDrill.id)
        .eq('player_id', selectedChild.id);

      if (error) throw error;

      setSuccess('Drill marked as complete!');
      handleCloseModal();
      await loadAssignedDrills(selectedChild.id);

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error marking drill complete:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: COLORS.background,
    },
    header: {
      backgroundColor: COLORS.white,
      borderBottom: '1px solid #e0e0e0',
      padding: isDesktop ? '20px 40px' : '16px',
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      justifyContent: 'space-between',
      alignItems: isDesktop ? 'center' : 'stretch',
      gap: isDesktop ? '0' : '12px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerLogo: {
      height: isDesktop ? '56px' : '45px',
      width: isDesktop ? 'auto' : '45px',
      objectFit: 'contain',
    },
    mobileHeaderContainer: {
      display: 'grid',
      gridTemplateColumns: '45px 1fr 45px',
      alignItems: 'center',
      width: '100%',
      gap: '12px',
    },
    mobileHeaderTitle: {
      textAlign: 'center',
      overflow: 'visible',
    },
    headerTitle: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: COLORS.textDark,
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    content: {
      padding: isDesktop ? '40px' : '16px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    buttonGroup: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: SPACING.sm,
      width: isDesktop ? 'auto' : '100%',
    },
    button: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
      width: isDesktop ? 'auto' : '100%',
    },
    secondaryButton: {
      minHeight: '44px',
      padding: `${SPACING.sm} ${SPACING.md}`,
      backgroundColor: COLORS.white,
      color: COLORS.primary,
      border: `2px solid ${COLORS.primary}`,
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      fontWeight: '500',
      width: isDesktop ? 'auto' : '100%',
    },
    childSelector: {
      marginBottom: SPACING.lg,
      backgroundColor: COLORS.white,
      padding: isDesktop ? SPACING.lg : SPACING.md,
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    selectorLabel: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: SPACING.sm,
      color: COLORS.textDark,
    },
    select: {
      width: '100%',
      minHeight: '44px',
      padding: SPACING.sm,
      fontSize: '16px',
      border: `1px solid ${COLORS.border}`,
      borderRadius: '4px',
      backgroundColor: COLORS.white,
      cursor: 'pointer',
    },
    emptyState: {
      textAlign: 'center',
      padding: SPACING.xl,
      backgroundColor: COLORS.white,
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    emptyStateTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: SPACING.md,
      color: COLORS.textDark,
    },
    emptyStateText: {
      fontSize: '16px',
      color: COLORS.textMuted,
      marginBottom: SPACING.lg,
    },
    card: {
      backgroundColor: COLORS.white,
      padding: isDesktop ? SPACING.lg : SPACING.md,
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: SPACING.lg,
    },
    cardTitle: {
      fontSize: isDesktop ? '22px' : '20px',
      fontWeight: 'bold',
      marginBottom: SPACING.md,
      color: COLORS.textDark,
    },
    errorMessage: {
      padding: SPACING.md,
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      marginBottom: SPACING.md,
    },
    successMessage: {
      padding: SPACING.md,
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      marginBottom: SPACING.md,
    },
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.container}>
      <ImpersonationBanner />
      {/* Header */}
      <div style={styles.header}>
        {isDesktop ? (
          <>
            <div style={styles.headerLeft}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <h1 style={styles.headerTitle}>Parent Dashboard</h1>
            </div>
            <DashboardNavigation />
          </>
        ) : (
          <div style={styles.mobileHeaderContainer}>
            <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
            <div style={styles.mobileHeaderTitle}>
              <MobileHeaderDropdown />
            </div>
            <AvatarMenu />
          </div>
        )}
      </div>

      <div style={styles.content}>
        {/* Error/Success Messages */}
        {error && <div style={styles.errorMessage}>{error}</div>}
        {success && <div style={styles.successMessage}>{success}</div>}

        {/* Child Selector */}
        {children.length > 0 ? (
          <div style={styles.childSelector}>
            <div
              style={{
                display: 'flex',
                flexDirection: isDesktop ? 'row' : 'column',
                justifyContent: 'space-between',
                alignItems: isDesktop ? 'center' : 'flex-start',
                gap: SPACING.md,
              }}
            >
              <div style={{ flex: 1, width: isDesktop ? 'auto' : '100%' }}>
                <label style={styles.selectorLabel}>Select Child:</label>
                <select
                  style={styles.select}
                  value={selectedChild?.id || ''}
                  onChange={(e) => {
                    const child = children.find((c) => c.id === e.target.value);
                    setSelectedChild(child);
                  }}
                >
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.full_name}
                      {child.is_dependent && ' (Managed)'}
                      {child.teams?.name && ` - ${child.teams.name}`}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: SPACING.sm,
                  marginTop: isDesktop ? '24px' : '0',
                  flexDirection: isDesktop ? 'row' : 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: isDesktop ? 'auto' : '100%',
                }}
              >
                <button style={styles.secondaryButton} onClick={() => setShowChildManagement(true)}>
                  Manage Players
                </button>
                <button
                  style={styles.secondaryButton}
                  onClick={() => setShowLinkRequestModal(true)}
                >
                  Link Existing Player
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <h2 style={styles.emptyStateTitle}>No Children Found</h2>
            <p style={styles.emptyStateText}>
              You don't have any children added yet. Click the button below to add your first child.
            </p>
            <button style={styles.button} onClick={() => setShowChildManagement(true)}>
              Add Your First Child
            </button>
          </div>
        )}

        {/* Drills and Stats (only show if child is selected) */}
        {selectedChild && (
          <>
            {/* Assigned Drills Card */}
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>Assigned Drills for {selectedChild.full_name}</h2>
              {assignedDrills.length === 0 ? (
                <p style={{ color: COLORS.textMuted }}>No drills assigned yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
                  {assignedDrills.map((assignment) => (
                    <DrillCard
                      key={assignment.id}
                      assignment={assignment}
                      onViewDetails={handleViewDetails}
                      onMarkComplete={handleMarkComplete}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Stats History Card */}
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>Stats History for {selectedChild.full_name}</h2>
              {playerStats.length === 0 ? (
                <p style={{ color: COLORS.textMuted }}>No stats recorded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
                  {playerStats.map((stat) => (
                    <StatCard key={stat.id} stat={stat} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Drill Completion Modal */}
        {showCompletionModal && selectedDrill && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: SPACING.md,
            }}
            onClick={handleCloseModal}
          >
            <div
              style={{
                backgroundColor: COLORS.white,
                padding: isDesktop ? SPACING.xl : SPACING.lg,
                borderRadius: '8px',
                maxWidth: '500px',
                width: isDesktop ? '100%' : '95vw',
                maxHeight: '90vh',
                overflow: 'auto',
                boxSizing: 'border-box',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0, marginBottom: SPACING.md }}>Mark Drill Complete</h2>
              <p style={{ marginBottom: SPACING.md }}>
                <strong>{selectedDrill.drills.name}</strong>
              </p>
              <form onSubmit={handleSubmitCompletion}>
                <div style={{ marginBottom: SPACING.md }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: SPACING.sm,
                      fontWeight: '600',
                    }}
                  >
                    Notes (Optional):
                  </label>
                  <textarea
                    value={playerNotes}
                    onChange={(e) => setPlayerNotes(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: SPACING.sm,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '4px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                    placeholder="Add any notes about completing this drill..."
                  />
                </div>
                {error && <div style={styles.errorMessage}>{error}</div>}
                <div style={{ display: 'flex', gap: SPACING.sm, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    style={{
                      ...styles.secondaryButton,
                      width: 'auto',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      ...styles.button,
                      width: 'auto',
                      opacity: submitting ? 0.6 : 1,
                    }}
                  >
                    {submitting ? 'Marking Complete...' : 'Mark as Complete'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Drill Details Modal */}
        {showDetailsModal && selectedDrill && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: SPACING.md,
            }}
            onClick={handleCloseModal}
          >
            <div
              style={{
                backgroundColor: COLORS.white,
                padding: isDesktop ? SPACING.xl : SPACING.lg,
                borderRadius: '8px',
                maxWidth: '600px',
                width: isDesktop ? '100%' : '95vw',
                maxHeight: '90vh',
                overflow: 'auto',
                boxSizing: 'border-box',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0, marginBottom: SPACING.md }}>
                {selectedDrill.drills.name}
              </h2>

              {/* Video Player */}
              {drillVideoUrl && (
                <div style={{ marginBottom: SPACING.lg }}>
                  <VideoPlayer
                    videoUrl={drillVideoUrl}
                    thumbnailUrl={drillThumbnailUrl}
                    title={selectedDrill.drills?.name}
                  />
                </div>
              )}

              <div style={{ marginBottom: SPACING.md }}>
                <p>
                  <strong>Description:</strong>
                </p>
                <p>{selectedDrill.drills.description || 'No description available'}</p>
              </div>
              <div style={{ marginBottom: SPACING.md }}>
                <strong>Category:</strong> {selectedDrill.drills.category}
              </div>
              <div style={{ marginBottom: SPACING.md }}>
                <strong>Difficulty:</strong> {selectedDrill.drills.difficulty_level}
              </div>
              <div style={{ marginBottom: SPACING.md }}>
                <strong>Assigned Date:</strong> {formatDate(selectedDrill.assigned_date)}
              </div>
              {selectedDrill.due_date && (
                <div style={{ marginBottom: SPACING.md }}>
                  <strong>Due Date:</strong> {formatDate(selectedDrill.due_date)}
                </div>
              )}
              {selectedDrill.coach_notes && (
                <div style={{ marginBottom: SPACING.md }}>
                  <strong>Coach Notes:</strong>
                  <p>{selectedDrill.coach_notes}</p>
                </div>
              )}
              {selectedDrill.completed && selectedDrill.player_notes && (
                <div style={{ marginBottom: SPACING.md }}>
                  <strong>Your Notes:</strong>
                  <p>{selectedDrill.player_notes}</p>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING.sm }}>
                <button onClick={handleCloseModal} style={styles.button}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Child Management Modal */}
      {showChildManagement && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: SPACING.md,
          }}
          onClick={() => setShowChildManagement(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <ChildManagementCard
              onClose={() => setShowChildManagement(false)}
              onChildrenChanged={loadChildren}
            />
          </div>
        </div>
      )}

      {/* Family Link Request Modal */}
      {showLinkRequestModal && (
        <FamilyLinkRequestModal
          onClose={() => setShowLinkRequestModal(false)}
          onRequestSent={loadChildren}
        />
      )}

      <Footer />
    </div>
  );
};

export default ParentDashboardPage;
